import { Modal } from "@/components/modal/Modal";
import { useCreateRelease } from "@/hooks/useCreateRelease";
import type { AlbumKind } from "@/lib/lutely-api/gen/dtos";
import { Flex, Spinner, Text } from "@radix-ui/themes";
import { useState } from "react";
import { RELEASE_KIND_OPTIONS } from "./releaseKindOptions";

type NewReleaseChooserProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

/** Asks Single or Album, then creates the release and opens it. */
export default function NewReleaseChooser({
  open,
  onOpenChange,
}: NewReleaseChooserProps) {
  const {
    createRelease,
    isCreatingRelease: isCreating,
    canCreate,
  } = useCreateRelease();
  const [chosen, setChosen] = useState<AlbumKind | null>(null);

  const choose = async (kind: AlbumKind) => {
    setChosen(kind);
    await createRelease(kind);
    setChosen(null);
    onOpenChange(false);
  };

  return (
    <Modal
      open={open}
      onOpenChange={(next) => !isCreating && onOpenChange(next)}
      title="New release"
      size="sm"
    >
      <Flex direction="column" className="gap-[10px]">
        {RELEASE_KIND_OPTIONS.map(({ kind, title, description, Icon }) => (
          <button
            key={kind}
            type="button"
            disabled={!canCreate || isCreating}
            onClick={() => choose(kind)}
            className="flex w-full cursor-pointer items-center gap-[12px] rounded-[10px] border border-border-tertiary bg-white px-[14px] py-[12px] text-left transition-colors hover:border-brand-primary hover:bg-background-tertiary disabled:cursor-default disabled:opacity-60"
            data-testid={`new-release-${kind.toLowerCase()}`}
          >
            <Flex className="h-[36px] w-[36px] flex-none items-center justify-center rounded-[8px] bg-background-tertiary text-text-primary">
              <Icon size={20} />
            </Flex>
            <Flex direction="column" className="min-w-0 flex-1">
              <Text className="text-[15px] font-semibold text-text-primary">
                {title}
              </Text>
              <Text className="text-[13px] text-text-secondary">
                {description}
              </Text>
            </Flex>
            {chosen === kind && <Spinner size="2" />}
          </button>
        ))}
      </Flex>
    </Modal>
  );
}
